import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, Calendar, Briefcase, Plus, Copy, Trash2, Send, X, Info, ChevronDown } from 'lucide-react';

const OmegaTimesheetSubmit = () => {
  const [projects, setProjects] = useState([]);
  const [workSubtypes, setWorkSubtypes] = useState([]);
  const [workCategories, setWorkCategories] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [forms, setForms] = useState([{
    projekt: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '08:00',
    end_time: '16:00',
    break_duration: 30,
    category: '',
    type: '',
    admin_type: '',
    note: ''
  }]);
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [visibleDays, setVisibleDays] = useState(7);
  const [showInfoBox, setShowInfoBox] = useState(true);

  useEffect(() => {
    fetchProjects();
    fetchWorkSubtypes();
    fetchVisibilitySettings();
    fetchWorkCategories();
    fetchWorkTypes();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/user-active-projects/`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setProjects(response.data || []);
    } catch (error) { console.error('Error fetching projects:', error); }
  };

  const fetchWorkSubtypes = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/work-subtypes/`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setWorkSubtypes((response.data || []).filter((st) => st.is_active));
    } catch (error) { console.error('Error fetching work subtypes:', error); }
  };

  const fetchVisibilitySettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/visibility-settings/me/`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setVisibleDays(response.data.days_visible || 7);
    } catch (error) { setVisibleDays(7); }
  };

  const fetchWorkCategories = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/work-categories/`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setWorkCategories(response.data || []);
    } catch (error) { console.error('Error fetching work categories:', error); }
  };

  const fetchWorkTypes = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/work-types/`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      setWorkTypes(response.data || []);
    } catch (error) { console.error('Error fetching work types:', error); }
  };

  const calculateWorkedHours = (startTime, endTime, breakDuration) => {
    if (!startTime || !endTime) return '0h 0min';
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    let totalMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    if (totalMinutes < 0) totalMinutes += 24 * 60;
    totalMinutes -= breakDuration;
    return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}min`;
  };

  const handleFormChange = (index, field, value) => {
    const newForms = [...forms];
    newForms[index] = { ...newForms[index], [field]: value };
    if (field === 'category') {
      newForms[index].type = '';
      newForms[index].admin_type = '';
    }
    if (field === 'type') {
      const selectedType = workTypes.find(t => t.code === value);
      if (selectedType) {
        const availableSubtypes = workSubtypes.filter(st => st.work_type_code === selectedType.code);
        if (availableSubtypes.length === 0) newForms[index].admin_type = '';
      }
    }
    setForms(newForms);
  };

  const addForm = () => {
    const lastForm = forms[forms.length - 1];
    const [endHour, endMin] = lastForm.end_time.split(':').map(Number);
    let newEndHour = endHour + 4;
    if (newEndHour > 23) newEndHour = 23;
    const newEndTime = `${String(newEndHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
    setForms([...forms, {
      projekt: lastForm.projekt,
      date: lastForm.date,
      start_time: lastForm.end_time,
      end_time: newEndTime,
      break_duration: 0,
      category: 'Work',
      type: 'Work(Day)',
      admin_type: '',
      note: ''
    }]);
  };

  const removeForm = (index) => {
    if (forms.length === 1) { alert('You must have at least one form'); return; }
    setForms(forms.filter((_, i) => i !== index));
  };

  const copyForm = (index) => {
    const formToCopy = forms[index];
    setForms([...forms, { ...formToCopy, note: '' }]);
  };

  const validateForm = (form, index) => {
    const formErrors = [];
    if (!form.projekt) formErrors.push(`Entry #${index + 1}: Select a project`);
    if (!form.date) {
      formErrors.push(`Entry #${index + 1}: Select a date`);
    } else {
      const selectedDateStr = form.date;
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      const minDate = new Date(today);
      minDate.setDate(minDate.getDate() - visibleDays);
      const minDateStr = minDate.toISOString().split('T')[0];
      if (selectedDateStr < minDateStr || selectedDateStr > todayStr) {
        formErrors.push(`Entry #${index + 1}: Date must be within the last ${visibleDays} days`);
      }
    }
    if (!form.start_time || !form.end_time) formErrors.push(`Entry #${index + 1}: Select start and end times`);
    if (form.break_duration < 0 || form.break_duration > 480) formErrors.push(`Entry #${index + 1}: Break must be between 0 and 480 minutes`);
    return formErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    setSuccess('');
    const allErrors = [];
    forms.forEach((form, index) => allErrors.push(...validateForm(form, index)));
    if (allErrors.length > 0) { setErrors(allErrors); return; }
    setLoading(true);
    const formData = new FormData();
    formData.append('form-TOTAL_FORMS', forms.length.toString());
    formData.append('form-INITIAL_FORMS', '0');
    formData.append('form-MAX_NUM_FORMS', '');
    forms.forEach((form, index) => {
      Object.entries(form).forEach(([key, value]) => formData.append(`form-${index}-${key}`, value.toString()));
    });
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/omega/submit/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      setSuccess(`Successfully added ${forms.length} timesheet(s)!`);
      setForms([{
        projekt: '', date: new Date().toISOString().split('T')[0], start_time: '08:00', end_time: '16:00',
        break_duration: 30, category: 'Work', type: 'Work(Day)', admin_type: '', note: ''
      }]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(Array.isArray(error.response.data.errors) ? error.response.data.errors : [error.response.data.errors]);
      } else if (error.response?.data?.error) {
        setErrors([error.response.data.error]);
      } else {
        setErrors(['An error occurred while saving timesheets.']);
      }
    } finally { setLoading(false); }
  };

  const getDateRangeInfo = () => {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(minDate.getDate() - visibleDays);
    return { minDate: minDate.toISOString().split('T')[0], maxDate: today.toISOString().split('T')[0] };
  };

  const dateRange = getDateRangeInfo();

  return (
    <div className="p-6">
      <div className="max-w-3xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black text-slate-900">Submit Timesheets</h1>
          <p className="text-xs text-slate-500">Add new timesheet entries for your work</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1.5 bg-brand-100 text-brand-600 rounded-xl text-xs font-bold">
            {forms.length} {forms.length === 1 ? 'Entry' : 'Entries'}
          </span>
        </div>
      </div>

      {/* Info Box */}
      {showInfoBox && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Info className="h-4 w-4 text-blue-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> You can add multiple entries for the same day (e.g., 8:00-12:00 Project A, 12:00-16:00 Project B)
            </p>
          </div>
          <button onClick={() => setShowInfoBox(false)} className="p-1 rounded-lg hover:bg-blue-100 text-blue-400">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Success */}
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
            <svg className="h-4 w-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-sm font-medium text-emerald-800">{success}</p>
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-lg bg-red-100 flex items-center justify-center">
              <X className="h-3 w-3 text-red-600" />
            </div>
            <p className="text-sm font-bold text-red-800">Please fix the following errors:</p>
          </div>
          <ul className="list-disc list-inside space-y-1 ml-8">
            {errors.map((error, index) => (
              <li key={index} className="text-sm text-red-700">{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {forms.map((form, index) => {
          const workedHours = calculateWorkedHours(form.start_time, form.end_time, form.break_duration);
          const selectedType = workTypes.find(t => t.code === form.type);
          const filteredSubtypes = selectedType ? workSubtypes.filter(st => st.work_type_code === selectedType.code) : [];
          const requiresAdminType = filteredSubtypes.length > 0;
          const filteredTypes = form.category ? workTypes.filter(wt => wt.category_name === form.category) : workTypes;

          return (
            <div key={index} className={`bg-white rounded-2xl border ${index === 0 ? 'border-brand-200' : 'border-steel-200'} shadow-sm overflow-hidden`}>
              {/* Card Header */}
              <div className="px-4 py-3 border-b border-steel-100 bg-steel-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${index === 0 ? 'bg-brand-100' : 'bg-steel-200'}`}>
                    <span className={`text-xs font-black ${index === 0 ? 'text-brand-600' : 'text-slate-600'}`}>#{index + 1}</span>
                  </div>
                  <span className="text-sm font-bold text-slate-900">Entry #{index + 1}</span>
                  {index === 0 && <span className="px-2 py-0.5 bg-brand-100 text-brand-600 rounded-md text-[9px] font-bold uppercase">Main</span>}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => copyForm(index)}
                    className="h-7 px-2 flex items-center gap-1 text-slate-600 hover:bg-steel-100 rounded-lg transition-colors"
                    title="Copy entry"
                  >
                    <Copy size={14} />
                    <span className="text-xs font-medium">Copy</span>
                  </button>
                  {forms.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeForm(index)}
                      className="h-7 px-2 flex items-center gap-1 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove entry"
                    >
                      <Trash2 size={14} />
                      <span className="text-xs font-medium">Remove</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4 space-y-4">
                {/* Project & Date - only first entry */}
                {index === 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                        Project <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={form.projekt}
                        onChange={(e) => handleFormChange(index, 'projekt', e.target.value)}
                        className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"
                      >
                        <option value="">-- Select project --</option>
                        {projects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-[10px] font-bold text-slate-700 uppercase tracking-widest">
                          Date <span className="text-red-500">*</span>
                        </label>
                        <span className="text-[9px] text-slate-400">Last {visibleDays} days</span>
                      </div>
                      <input
                        type="date"
                        value={form.date}
                        onChange={(e) => handleFormChange(index, 'date', e.target.value)}
                        min={dateRange.minDate}
                        max={dateRange.maxDate}
                        className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                    </div>
                  </div>
                )}

                {/* Time Row */}
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                      Start <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={form.start_time}
                      onChange={(e) => handleFormChange(index, 'start_time', e.target.value)}
                      className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                      End <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="time"
                      value={form.end_time}
                      onChange={(e) => handleFormChange(index, 'end_time', e.target.value)}
                      className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                      Break (min) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={form.break_duration}
                      onChange={(e) => handleFormChange(index, 'break_duration', parseInt(e.target.value) || 0)}
                      min="0"
                      max="480"
                      className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">Worked</label>
                    <div className="h-10 px-3 bg-brand-50 border border-brand-200 rounded-xl flex items-center">
                      <span className="text-sm font-black text-brand-600">{workedHours}</span>
                    </div>
                  </div>
                </div>

                {/* Category, Type, Note Row */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.category}
                      onChange={(e) => handleFormChange(index, 'category', e.target.value)}
                      className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"
                    >
                      <option value="">Select Category</option>
                      {workCategories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                      Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.type}
                      onChange={(e) => handleFormChange(index, 'type', e.target.value)}
                      disabled={!form.category}
                      className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white disabled:bg-steel-100 disabled:text-slate-400"
                    >
                      <option value="">Select Type</option>
                      {filteredTypes.map(wt => <option key={wt.id} value={wt.code}>{wt.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">Note</label>
                    <input
                      type="text"
                      value={form.note}
                      onChange={(e) => handleFormChange(index, 'note', e.target.value)}
                      maxLength={255}
                      placeholder="Optional note..."
                      className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                  </div>
                </div>

                {/* Subtype (if available) */}
                {requiresAdminType && (
                  <div>
                    <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1.5">
                      Subtype <span className="text-slate-400">(optional)</span>
                    </label>
                    <select
                      value={form.admin_type}
                      onChange={(e) => handleFormChange(index, 'admin_type', e.target.value)}
                      className="w-full h-10 px-3 border border-steel-200 rounded-xl text-sm font-medium text-slate-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white"
                    >
                      <option value="">-- Select Subtype --</option>
                      {filteredSubtypes.map(subtype => <option key={subtype.id} value={subtype.id}>{subtype.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Actions */}
        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={addForm}
            disabled={loading}
            className="h-10 px-4 flex items-center gap-2 bg-white border border-steel-200 text-slate-700 rounded-xl hover:bg-steel-50 font-bold text-sm transition-colors disabled:opacity-50"
          >
            <Plus size={16} />
            Add Another Entry
          </button>

          <button
            type="submit"
            disabled={loading}
            className="h-10 px-6 flex items-center gap-2 bg-brand-500 text-white rounded-xl hover:bg-brand-600 font-bold text-sm uppercase tracking-wide transition-all shadow-lg shadow-brand-500/20 disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send size={16} />
                <span>Send Timesheets ({forms.length})</span>
              </>
            )}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default OmegaTimesheetSubmit;
